import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Bookmark,
    Search,
    Loader2,
    RotateCcw,
    CheckCircle2,
    Trash2,
    AlertTriangle,
    Package,
} from "lucide-react";
import api from "../../lib/api";
import { imgSrc } from "../../lib/imgSrc";
import { OrderAddressModal } from "./OrderAddressModal";

/**
 * ProductHoldsPanel — Admin → Shop → Holds.
 *
 * Reserve stock for a specific customer ("Hold 3 stickers for Jane, coming
 * Sat"). Units come out of inventory the moment the hold is created so no
 * one else can grab them online. One-click "Return to inventory" restocks
 * on a no-show; "Mark picked up" just closes the hold.
 */
const currency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const STATUS_BADGE = {
    active: "bg-amber-100 text-amber-800 border-amber-200",
    released: "bg-blue-100 text-blue-800 border-blue-200",
    picked_up: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const STATUS_LABEL = {
    active: "Held",
    released: "Returned",
    picked_up: "Picked up",
};

const relTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diffH = (Date.now() - d.getTime()) / 3600000;
    if (diffH < 1) return `${Math.max(1, Math.round(diffH * 60))} min ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
};

const ProductHoldsPanel = () => {
    const [saleHold, setSaleHold] = useState(null);
    const [holds, setHolds] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("active");

    // Create form
    const [selectedProduct, setSelectedProduct] = useState("");
    const [qty, setQty] = useState(1);
    const [customer, setCustomer] = useState("");
    const [note, setNote] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [productSearch, setProductSearch] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadHolds = async (status = statusFilter) => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/holds?status=${status}`);
            setHolds(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            toast.error("Failed to load holds");
        } finally {
            setLoading(false);
        }
    };
    const loadProducts = async () => {
        try {
            const r = await api.get("/admin/products");
            setProducts(Array.isArray(r.data) ? r.data : []);
        } catch (e) { /* silent */ }
    };
    useEffect(() => { loadHolds(); loadProducts(); }, []);
    useEffect(() => { loadHolds(statusFilter); }, [statusFilter]);

    const filteredProducts = useMemo(() => {
        const q = productSearch.trim().toLowerCase();
        return products
            .filter((p) => p && p.id)
            .filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .slice(0, 40);
    }, [products, productSearch]);

    const selected = products.find((p) => p.id === selectedProduct);

    const resetForm = () => {
        setSelectedProduct("");
        setQty(1);
        setCustomer("");
        setNote("");
        setExpiresAt("");
        setProductSearch("");
    };

    const create = async () => {
        if (!selectedProduct) { toast.error("Pick a product first"); return; }
        const n = Math.max(1, Math.floor(Number(qty) || 0));
        setSubmitting(true);
        try {
            await api.post("/admin/holds", {
                product_id: selectedProduct,
                quantity: n,
                customer_name: customer.trim(),
                note: note.trim(),
                expires_at: expiresAt || null,
            });
            toast.success(`Reserved ${n} × ${selected?.name}`);
            resetForm();
            await Promise.all([loadHolds("active"), loadProducts()]);
            setStatusFilter("active");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create hold");
        } finally {
            setSubmitting(false);
        }
    };

    const release = async (h) => {
        if (!window.confirm(`Return ${h.quantity} × ${h.product_name} to inventory?`)) return;
        try {
            await api.post(`/admin/holds/${h.id}/release`);
            toast.success(`${h.quantity} units returned to inventory`);
            await Promise.all([loadHolds(statusFilter), loadProducts()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to release hold");
        }
    };
    const pickup = async (h, recordSale = false, customerAddress = null) => {
        if (recordSale && !customerAddress) { setSaleHold(h); return; }
        try {
            const r = await api.post(`/admin/holds/${h.id}/pickup`, {
                record_sale: recordSale,
                customer_address: customerAddress,
                notes: "",
            });
            const data = r.data || {};
            if (data.sale_recorded) {
                toast.success(`Hold closed & sale ${data.order_number} logged (${currency(data.total)})`);
            } else {
                toast.success(`Hold closed — ${h.customer_name || "customer"} picked up`);
            }
            await loadHolds(statusFilter);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const message = typeof detail === "string" ? detail : "Could not record pickup. Check the customer address and try again.";
            if (customerAddress) throw new Error(message);
            toast.error(message);
        }
    };
    const remove = async (h) => {
        if (!window.confirm("Delete this hold record permanently?")) return;
        try {
            await api.delete(`/admin/holds/${h.id}`);
            toast.success("Hold record deleted");
            await loadHolds(statusFilter);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete hold");
        }
    };

    const undoPickup = async (h) => {
        const proceed = window.confirm(
            `Undo pickup for ${h.quantity} × ${h.product_name}?\n\n` +
            (h.sale_order_number
                ? `This will VOID the linked sale (order ${h.sale_order_number}) and its journal entry, then flip the hold back to Active.`
                : `The hold will flip back to Active — units stay reserved.`) +
            `\n\nSafe to do if you clicked pickup on the wrong item.`
        );
        if (!proceed) return;
        try {
            const { data } = await api.post(`/admin/holds/${h.id}/undo-pickup`);
            const parts = ["Pickup undone"];
            if (data.order_voided) parts.push("sale voided");
            if (data.journal_entries_voided) parts.push("JE voided");
            toast.success(parts.join(" · "));
            await Promise.all([loadHolds(statusFilter), loadProducts()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Undo failed");
        }
    };

    const undoRelease = async (h) => {
        if (!window.confirm(`Undo release for ${h.quantity} × ${h.product_name}?\n\nWill re-reserve the units and flip the hold back to Active. Refused if stock isn't available anymore.`)) return;
        try {
            const { data } = await api.post(`/admin/holds/${h.id}/undo-release`);
            toast.success(`Release undone · ${data.units_re_reserved} units re-reserved`);
            await Promise.all([loadHolds(statusFilter), loadProducts()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Undo failed");
        }
    };

    const activeCount = holds.filter((h) => h.status === "active").length;
    const reservedUnits = holds.filter((h) => h.status === "active").reduce((s, h) => s + Number(h.quantity || 0), 0);

    return (
        <div className="space-y-6" data-testid="product-holds-panel">
            {saleHold && <OrderAddressModal name={saleHold.customer_name || saleHold.hold_number} onClose={() => setSaleHold(null)} onSave={async address => { await pickup(saleHold, true, address); setSaleHold(null); }} />}
            {/* Header */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                        <Bookmark className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium tracking-tight">Products on hold</h2>
                        <p className="text-sm text-[#4A5568] mt-1">
                            Reserve units for a specific customer — they come out of inventory immediately so no one else can buy them online.
                            One-click return if the customer doesn&apos;t show.
                        </p>
                    </div>
                </div>
            </div>

            {/* Create form */}
            <div className="bg-white border border-black/10 rounded-2xl p-5" data-testid="product-holds-create">
                <h3 className="font-heading text-lg text-[#1C252E] font-medium mb-4">Reserve a product</h3>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
                    {/* Product picker */}
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Product</label>
                        <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                            <input
                                type="text"
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                placeholder="Search by name or SKU"
                                data-testid="hold-product-search"
                                className="w-full pl-9 pr-3 py-2 bg-[#F4F1EA] border border-transparent rounded-lg text-sm focus:outline-none focus:bg-white focus:border-[#E53935]"
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-black/10 divide-y divide-black/5">
                            {filteredProducts.length === 0 ? (
                                <p className="p-4 text-sm text-[#4A5568] text-center">No matching products.</p>
                            ) : (
                                filteredProducts.map((p) => {
                                    const active = selectedProduct === p.id;
                                    const firstImg = Array.isArray(p.images) && p.images.length > 0
                                        ? (typeof p.images[0] === "string" ? p.images[0] : (p.images[0]?.url || ""))
                                        : (p.image_url || "");
                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => setSelectedProduct(p.id)}
                                            data-testid={`hold-product-option-${p.id}`}
                                            className={`w-full flex items-center gap-3 p-2 text-left transition-colors ${
                                                active ? "bg-[#E53935]/10" : "hover:bg-[#F4F1EA]"
                                            }`}
                                        >
                                            <div className="w-9 h-9 rounded-md bg-[#F4F1EA] overflow-hidden flex-shrink-0">
                                                {firstImg ? (
                                                    <img src={imgSrc(firstImg, 72, 70)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                                ) : null}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-[#1C252E] truncate">{p.name}</p>
                                                <p className="text-xs text-[#4A5568]">
                                                    {currency(p.price)} · stock {Number(p.stock || 0)}
                                                    {p.sku ? <span className="text-[#4A5568]/70"> · {p.sku}</span> : null}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Reservation details */}
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Quantity</label>
                            <input
                                type="number"
                                min="1"
                                value={qty}
                                onChange={(e) => setQty(e.target.value)}
                                data-testid="hold-qty"
                                className="w-full px-3 py-2 bg-white border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                            />
                            {selected && Number(qty) > Number(selected.stock || 0) && (
                                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> Only {Number(selected.stock || 0)} in stock.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Customer name</label>
                            <input
                                type="text"
                                value={customer}
                                onChange={(e) => setCustomer(e.target.value)}
                                placeholder="e.g. Jane Doe"
                                data-testid="hold-customer"
                                className="w-full px-3 py-2 bg-white border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Pickup by <span className="text-[#4A5568]/60 font-normal normal-case tracking-normal">(optional)</span></label>
                            <input
                                type="date"
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                                data-testid="hold-expires"
                                className="w-full px-3 py-2 bg-white border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Note</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="e.g. Called Tue, coming Sat 10am"
                                data-testid="hold-note"
                                rows={2}
                                className="w-full px-3 py-2 bg-white border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935] resize-none"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={create}
                            disabled={submitting || !selectedProduct}
                            data-testid="hold-submit"
                            className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-[#E53935] text-white hover:bg-[#c62828] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Reserving…</>
                            ) : (
                                <><Bookmark className="w-4 h-4" /> Reserve {qty > 0 ? `${qty} unit${qty === 1 ? "" : "s"}` : "units"}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Holds table */}
            <div className="bg-white border border-black/10 rounded-2xl p-5">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <h3 className="font-heading text-lg text-[#1C252E] font-medium">Holds</h3>
                        {statusFilter === "active" && activeCount > 0 && (
                            <p className="text-xs text-[#4A5568] mt-0.5">
                                <Package className="inline w-3 h-3 mr-0.5" /> {reservedUnits} unit{reservedUnits === 1 ? "" : "s"} reserved across {activeCount} hold{activeCount === 1 ? "" : "s"}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-1 bg-[#F4F1EA] rounded-lg p-1" role="tablist">
                        {["active", "released", "picked_up", "all"].map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setStatusFilter(s)}
                                data-testid={`hold-filter-${s}`}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${
                                    statusFilter === s ? "bg-white text-[#1C252E] shadow-sm" : "text-[#4A5568] hover:text-[#1C252E]"
                                }`}
                            >
                                {s === "picked_up" ? "Picked up" : s}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="py-12 text-center text-[#4A5568] flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading holds…
                    </div>
                ) : holds.length === 0 ? (
                    <div className="py-12 text-center text-sm text-[#4A5568]">
                        No {statusFilter === "all" ? "" : statusFilter} holds{statusFilter === "active" ? " right now — everything is available for purchase." : "."}
                    </div>
                ) : (
                    <div className="overflow-x-auto -mx-5">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-[#4A5568] border-b border-black/10">
                                    <th className="px-5 py-2 font-semibold">Hold</th>
                                    <th className="px-2 py-2 font-semibold">Product</th>
                                    <th className="px-2 py-2 font-semibold">Qty</th>
                                    <th className="px-2 py-2 font-semibold">For</th>
                                    <th className="px-2 py-2 font-semibold">Age</th>
                                    <th className="px-2 py-2 font-semibold">Status</th>
                                    <th className="px-5 py-2 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5">
                                {holds.map((h) => (
                                    <tr key={h.id} data-testid={`hold-row-${h.id}`} className="hover:bg-[#F4F1EA]/40">
                                        <td className="px-5 py-3 font-mono text-xs text-[#4A5568]">{h.hold_number}</td>
                                        <td className="px-2 py-3">
                                            <p className="font-medium text-[#1C252E]">{h.product_name}</p>
                                            {h.note ? <p className="text-xs text-[#4A5568]">{h.note}</p> : null}
                                        </td>
                                        <td className="px-2 py-3 font-medium">{h.quantity}</td>
                                        <td className="px-2 py-3">
                                            <p className="text-[#1C252E]">{h.customer_name || <span className="text-[#4A5568]/70 italic">unnamed</span>}</p>
                                            {h.expires_at ? <p className="text-xs text-[#4A5568]">by {h.expires_at.slice(0, 10)}</p> : null}
                                        </td>
                                        <td className="px-2 py-3 text-xs text-[#4A5568]">{relTime(h.created_at)}</td>
                                        <td className="px-2 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGE[h.status] || ""}`}>
                                                {STATUS_LABEL[h.status] || h.status}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-1.5 justify-end flex-wrap">
                                                {h.status === "active" ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => release(h)}
                                                            data-testid={`hold-release-${h.id}`}
                                                            className="px-2.5 py-1 text-xs rounded-md border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 flex items-center gap-1"
                                                        >
                                                            <RotateCcw className="w-3 h-3" /> Return
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => pickup(h, false)}
                                                            data-testid={`hold-pickup-${h.id}`}
                                                            className="px-2.5 py-1 text-xs rounded-md border border-black/10 bg-white text-[#4A5568] hover:bg-[#F4F1EA] flex items-center gap-1"
                                                            title="Close the hold — units already left inventory at reservation. Doesn't post to the register."
                                                        >
                                                            <CheckCircle2 className="w-3 h-3" /> Close
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => pickup(h, true)}
                                                            data-testid={`hold-pickup-log-${h.id}`}
                                                            className="px-2.5 py-1 text-xs rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 flex items-center gap-1 font-medium"
                                                            title="Close the hold and post a cash sale to the register. Margin routes to the GSF."
                                                        >
                                                            <CheckCircle2 className="w-3 h-3" /> Picked up · log {currency(Number(h.unit_price || 0) * Number(h.quantity || 0))}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {h.status === "picked_up" && (
                                                            <button
                                                                type="button"
                                                                onClick={() => undoPickup(h)}
                                                                data-testid={`hold-undo-pickup-${h.id}`}
                                                                className="px-2.5 py-1 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-1"
                                                                title={h.sale_order_number ? `Undo pickup — voids sale ${h.sale_order_number} + its JE, restores hold to Active` : "Undo pickup — restores hold to Active"}
                                                            >
                                                                <RotateCcw className="w-3 h-3" /> Undo pickup
                                                            </button>
                                                        )}
                                                        {h.status === "released" && (
                                                            <button
                                                                type="button"
                                                                onClick={() => undoRelease(h)}
                                                                data-testid={`hold-undo-release-${h.id}`}
                                                                className="px-2.5 py-1 text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-1"
                                                                title="Undo release — re-reserves the units and restores hold to Active"
                                                            >
                                                                <RotateCcw className="w-3 h-3" /> Undo release
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(h)}
                                                            data-testid={`hold-delete-${h.id}`}
                                                            className="p-1.5 rounded-md text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10"
                                                            aria-label="Delete hold"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductHoldsPanel;
